import { z } from 'zod';

// Citation schemas matching Anthropic's Citations API format.
// Factory returns fresh instances: a Zod instance reused within one schema
// tree converts to a non-top-level $ref, which OpenAI/Anthropic structured
// outputs reject.
export const makeCitationSchema = () =>
  z.union([
    z.object({
      type: z.literal('char_location'),
      cited_text: z.string(),
      document_index: z.number(),
      document_title: z.string().optional(),
      start_char_index: z.number(),
      end_char_index: z.number(),
      artifact_id: z.string().optional(), // ID from external_patient_artifacts table
    }),
    z.object({
      type: z.literal('page_location'),
      cited_text: z.string(),
      document_index: z.number(),
      document_title: z.string().optional(),
      start_page_number: z.number(),
      end_page_number: z.number(),
      artifact_id: z.string().optional(), // ID from external_patient_artifacts table
    }),
    z.object({
      type: z.literal('content_block_location'),
      cited_text: z.string(),
      document_index: z.number(),
      document_title: z.string().optional(),
      start_block_index: z.number(),
      end_block_index: z.number(),
      artifact_id: z.string().optional(), // ID from external_patient_artifacts table
    }),
  ]);

export const citationSchema = makeCitationSchema();

/**
 * Ties one phrase of a `supporting_evidence` bullet to the passage backing it.
 *
 * The `.describe()` text is load-bearing: it reaches the model as the
 * structured output's JSON Schema, which is how this gets populated without
 * editing every tenant's stored prompt template.
 */
export const makeEvidenceCitationSchema = () =>
  z.object({
    evidence_index: z
      .number()
      .int()
      .describe(
        "Zero-based index into this diagnosis's supporting_evidence array.",
      ),
    phrase: z
      .string()
      .describe(
        'The exact substring of that supporting_evidence entry this passage supports, copied character for character. Keep it to the specific claim (e.g. "absent in morning"), not the whole sentence.',
      ),
    cited_text: z
      .string()
      .describe(
        'The verbatim passage from the transcript or record that supports the phrase, copied character for character. Never paraphrase — an inexact quote cannot be located and will be discarded.',
      ),
    document_title: z
      .string()
      .optional()
      .describe('Source document, e.g. "Transcript" or "Lab Results".'),
    artifact_id: z.string().optional(),
  });

export const evidenceCitationSchema = makeEvidenceCitationSchema();

/**
 * One run of the model's own prose for an evidence bullet, optionally carrying
 * the passage backing exactly that run.
 *
 * This replaces `evidence_citations.phrase`, which asked the model to reproduce
 * a substring of its own bullet character for character so the server could
 * find it by search. Models often returned the source quote there instead —
 * indistinguishable from `cited_text` — which cannot be located and was
 * discarded. Measured over one day of local generations: claude-opus-4-6
 * resolved 67 of 68 phrases, gemini-3.6-flash only 6 of 15, and on one
 * appointment 0 of 8, collapsing every citation into an undifferentiated list.
 *
 * Segments make the association structural: offsets are computed by summing
 * segment lengths, so there is nothing to locate and nothing to get wrong.
 */
export const makeEvidenceSegmentSchema = () =>
  z.object({
    text: z
      .string()
      .describe(
        "One run of YOUR OWN wording. Joined in order with this bullet's other segments — no separators added — these form the sentence the clinician reads.",
      ),
    cited_text: z
      .string()
      .optional()
      .describe(
        'The verbatim passage from the transcript or record backing THIS run of text, copied character for character. Omit on connective wording that nothing backs. Never paraphrase.',
      ),
    document_title: z
      .string()
      .optional()
      .describe(
        'Source document for cited_text, e.g. "Transcript" or "Lab Results".',
      ),
    artifact_id: z.string().optional(),
  });

export const makeEvidenceBulletSchema = () =>
  z.object({
    segments: z
      .array(makeEvidenceSegmentSchema())
      .describe(
        'The bullet split into runs. Give each cited claim its own segment so the citation attaches to exactly those words, and leave uncited wording in segments without cited_text.',
      ),
  });

export const evidenceSegmentSchema = makeEvidenceSegmentSchema();
export const evidenceBulletSchema = makeEvidenceBulletSchema();

export const differentialDiagnosisSchema = z.object({
  candidate_diagnosis: z.string(),
  /**
   * Optional rather than required: this schema also validates
   * DIAGNOSES_JUSTIFICATION output and parses stored outputs generated before
   * segments existed. Requiring it would fail both. The `.describe()` is what
   * drives the model to populate it.
   */
  evidence: z
    .array(makeEvidenceBulletSchema())
    .optional()
    .describe(
      'The supporting evidence, one entry per bullet, each split into cited and uncited segments.',
    ),
  /**
   * Derived from `evidence` in `enrich-insights-with-stats`, not written by the
   * model. Kept in the stored shape because deployed web bundles and the
   * extension render it directly.
   */
  supporting_evidence: z.array(z.string()).optional(),
  signal_strength: z.enum(['strong', 'moderate', 'weak']),
  clinical_urgency: z.enum(['strong', 'moderate', 'weak']),
  current_differential_gap: z.string(),
  suggested_next_steps: z.string(),
  key_uncertainty: z.string(),
  gates_passed: z.array(z.string()),
  citations: z.array(citationSchema).optional(), // Overall citations for the diagnosis, separate from those tied to specific indicators
  evidence_citations: z.array(evidenceCitationSchema).optional(),
});

export const differentialDiagnosesSchema = z.array(differentialDiagnosisSchema);

// Lenient variants for rendering stored outputs: fields added after an output
// was generated fall back to defaults instead of dropping the whole output.
// Generation/extraction must keep using the strict schemas above.
export const differentialDiagnosisDisplaySchema = z.object({
  candidate_diagnosis: z.string(),
  supporting_evidence: z.array(z.string()).catch([]),
  /** Absent on outputs generated before segments shipped. */
  evidence: z.array(makeEvidenceBulletSchema()).optional().catch(undefined),
  signal_strength: z.enum(['strong', 'moderate', 'weak']).catch('moderate'),
  clinical_urgency: z.enum(['strong', 'moderate', 'weak']).catch('moderate'),
  current_differential_gap: z.string().catch(''),
  suggested_next_steps: z.string().catch(''),
  key_uncertainty: z.string().catch(''),
  gates_passed: z.array(z.string()).catch([]),
  citations: z.array(makeCitationSchema()).optional().catch(undefined),
  evidence_citations: z
    .array(makeEvidenceCitationSchema())
    .optional()
    .catch(undefined),
});

export const differentialDiagnosesDisplaySchema = z.array(
  differentialDiagnosisDisplaySchema,
);

export type DifferentialDiagnosisDisplaySchema = z.infer<
  typeof differentialDiagnosisDisplaySchema
>;
export type DifferentialDiagnosesDisplaySchema = z.infer<
  typeof differentialDiagnosesDisplaySchema
>;

export type Citation = z.infer<typeof citationSchema>;
export type EvidenceCitation = z.infer<typeof evidenceCitationSchema>;
export type DifferentialDiagnosisOutputSchema = z.infer<
  typeof differentialDiagnosisSchema
>;

export type DifferentialDiagnosesOutputSchema = z.infer<
  typeof differentialDiagnosesSchema
>;
